import React, { useState, useEffect, useRef, useCallback } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro'
import { Toast } from 'primereact/toast'
import { Avatar } from 'primereact/avatar'
import NameInitials from '../../helpers/nameInitials'
import performanceService from '../../services/data/performance.service'
import employeeService from '../../services/data/employee.service'

/**
 * Seleção de pares para avaliação 360º.
 *
 * Props:
 *   participants  — array de PerformanceReview objects (cada um com employees[])
 *   peerData      — array de peers já carregados (objeto.peers do template serializer).
 *                   Quando fornecido, evita N requisições na montagem; após mutações
 *                   apenas o review afetado é recarregado (1 requisição).
 *                   Se omitido, cai no comportamento legado (1 req por review).
 *   onUpdate      — callback chamado após qualquer adição/remoção de par
 */
export default function PeerSelect({ participants, peerData, onUpdate }) {
    const toast = useRef(null)
    const debounceRef = useRef({})
    // guard para evitar re-fetch quando participants muda de referência mas contém os mesmos reviews
    const loadedKeyRef = useRef(null)

    // { [reviewId]: { [employeeId]: [peerStep, ...] } }
    const [peerMap, setPeerMap] = useState({})
    const [adding, setAdding] = useState({})
    const [selectedPeer, setSelectedPeer] = useState({})
    const [empSearch, setEmpSearch] = useState('')
    const [peerQuery, setPeerQuery] = useState({})
    const [peerSuggestions, setPeerSuggestions] = useState({})

    // Quando peerData é fornecido, constrói peerMap sem nenhuma requisição
    useEffect(() => {
        if (!peerData) return
        setPeerMap(buildMapFromPeerData(peerData))
    }, [peerData])

    // Fallback: carrega via API quando peerData não é fornecido
    const loadAllPeers = useCallback(() => {
        if (!participants || participants.length === 0) return
        const key = participants.map((r) => r.id).sort().join(',')
        if (key === loadedKeyRef.current) return
        loadedKeyRef.current = key

        const promises = participants.map((review) =>
            performanceService.listPeerSteps(review.id).then((steps) => ({
                reviewId: review.id,
                steps,
            }))
        )
        Promise.all(promises).then((results) => {
            const map = {}
            results.forEach(({ reviewId, steps }) => {
                map[reviewId] = {}
                steps.forEach((step) => {
                    const empId = step.assess_who
                    if (!map[reviewId][empId]) map[reviewId][empId] = []
                    map[reviewId][empId].push(step)
                })
            })
            setPeerMap(map)
        })
    }, [participants])

    useEffect(() => {
        if (!peerData) {
            loadAllPeers()
        }
    }, [peerData, loadAllPeers])

    function buildMapFromPeerData(data) {
        const map = {}
        ;(data || []).forEach((item) => {
            const reviewId = item.review_id
            if (!map[reviewId]) map[reviewId] = {}
            map[reviewId][item.employee_id] = (item.peers || []).map((p) => ({
                id: p.step_id,
                assess_who: item.employee_id,
                action_for: p.peer_id,
                peer_name: p.peer_name,
                peer_photo: null,
                status: p.status,
            }))
        })
        return map
    }

    // Após mutação, recarrega apenas o review afetado (1 req)
    function reloadReview(reviewId) {
        performanceService.listPeerSteps(reviewId).then((steps) => {
            setPeerMap((prev) => {
                const reviewMap = {}
                steps.forEach((step) => {
                    const empId = step.assess_who
                    if (!reviewMap[empId]) reviewMap[empId] = []
                    reviewMap[empId].push(step)
                })
                return { ...prev, [reviewId]: reviewMap }
            })
        })
    }

    function getKey(reviewId, empId) {
        return `${reviewId}_${empId}`
    }

    function searchPeers(key, query) {
        setPeerQuery((prev) => ({ ...prev, [key]: query }))
        setSelectedPeer((prev) => ({ ...prev, [key]: '' }))
        if (debounceRef.current[key]) clearTimeout(debounceRef.current[key])
        if (!query.trim()) {
            setPeerSuggestions((prev) => ({ ...prev, [key]: [] }))
            return
        }
        debounceRef.current[key] = setTimeout(() => {
            employeeService.autoCompleteEmployee(query).then((res) => {
                setPeerSuggestions((prev) => ({ ...prev, [key]: res || [] }))
            }).catch(() => {})
        }, 300)
    }

    function selectSuggestion(key, emp) {
        setSelectedPeer((prev) => ({ ...prev, [key]: emp.id }))
        setPeerQuery((prev) => ({ ...prev, [key]: emp.user }))
        setPeerSuggestions((prev) => ({ ...prev, [key]: [] }))
    }

    function handleAddPeer(review, employee) {
        const key = getKey(review.id, employee.id)
        const peerId = selectedPeer[key]
        if (!peerId) return

        setAdding((prev) => ({ ...prev, [key]: true }))
        performanceService.addPeerStep({
            review_id: review.id,
            employee_id: employee.id,
            peer_id: peerId,
            due_date: review.end_date,
        }).then(() => {
            setSelectedPeer((prev) => ({ ...prev, [key]: '' }))
            setPeerQuery((prev) => ({ ...prev, [key]: '' }))
            setPeerSuggestions((prev) => ({ ...prev, [key]: [] }))
            reloadReview(review.id)
            if (onUpdate) onUpdate()
        }).catch((err) => {
            const msg = err?.response?.data ? JSON.stringify(err.response.data) : 'Erro ao adicionar par.'
            toast.current.show({ severity: 'warn', summary: 'Ops', detail: msg, life: 5000 })
        }).finally(() => {
            setAdding((prev) => ({ ...prev, [key]: false }))
        })
    }

    function handleRemovePeer(stepId, reviewId) {
        performanceService.removePeerStep(stepId).then(() => {
            reloadReview(reviewId)
            if (onUpdate) onUpdate()
        }).catch((err) => {
            const msg = err?.response?.data ? JSON.stringify(err.response.data) : 'Erro ao remover par.'
            toast.current.show({ severity: 'warn', summary: 'Ops', detail: msg, life: 5000 })
        })
    }

    if (!participants || participants.length === 0) return null

    const searchLower = empSearch.toLowerCase()

    return (
        <div>
            <Toast ref={toast} position="top-right" />

            <div className="mb-3">
                <div className="input-group input-group-sm">
                    <span className="input-group-text bg-transparent">
                        <FontAwesomeIcon icon={solid('magnifying-glass')} className="text-muted" />
                    </span>
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Buscar colaborador avaliado..."
                        value={empSearch}
                        onChange={(e) => setEmpSearch(e.target.value)}
                    />
                    {empSearch && (
                        <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => setEmpSearch('')}
                        >
                            <FontAwesomeIcon icon={solid('times')} />
                        </button>
                    )}
                </div>
            </div>

            {participants.map((review) => {
                const filteredEmployees = (review.employees || []).filter((emp) => {
                    if (!searchLower) return true
                    return (emp.user?.full_name || '').toLowerCase().includes(searchLower)
                })

                if (filteredEmployees.length === 0) return null

                return (
                    <div key={review.id} className="mb-4">
                        {filteredEmployees.map((emp) => {
                            const key = getKey(review.id, emp.id)
                            const peers = peerMap[review.id]?.[emp.id] || []
                            const peerIds = new Set(peers.map((p) => p.action_for))
                            const suggestions = (peerSuggestions[key] || []).filter(
                                (e) => String(e.id) !== String(emp.id) && !peerIds.has(String(e.id))
                            )

                            return (
                                <div key={emp.id} className="border rounded p-3 mb-3">
                                    <div className="d-flex align-items-center mb-2">
                                        <Avatar
                                            image={emp.user?.photo || null}
                                            label={!emp.user?.photo ? NameInitials(emp.user?.full_name || '') : null}
                                            shape="circle"
                                            size="normal"
                                            className="me-2"
                                        />
                                        <span className="fw-bold">{emp.user?.full_name || emp.id}</span>
                                        <span className="ms-2 text-muted small">({peers.length} par{peers.length !== 1 ? 'es' : ''})</span>
                                    </div>

                                    {peers.length > 0 && (
                                        <div className="mb-2 d-flex flex-wrap gap-2">
                                            {peers.map((peerStep) => (
                                                <span key={peerStep.id} className="badge bg-light text-dark border d-flex align-items-center gap-1 px-2 py-1">
                                                    <Avatar
                                                        image={peerStep.peer_photo || null}
                                                        label={!peerStep.peer_photo ? NameInitials(peerStep.peer_name || '') : null}
                                                        shape="circle"
                                                        size="small"
                                                    />
                                                    <span className="ms-1">{peerStep.peer_name}</span>
                                                    {peerStep.status !== 'RE' && (
                                                        <button
                                                            type="button"
                                                            className="btn btn-link btn-sm text-danger p-0 ms-1"
                                                            onClick={() => handleRemovePeer(peerStep.id, review.id)}
                                                            title="Remover par"
                                                        >
                                                            <FontAwesomeIcon icon={solid('times')} />
                                                        </button>
                                                    )}
                                                </span>
                                            ))}
                                        </div>
                                    )}

                                    <div className="d-flex gap-2 align-items-center">
                                        <div className="flex-grow-1 position-relative">
                                            <input
                                                type="text"
                                                className="form-control form-control-sm"
                                                placeholder="Buscar par avaliador..."
                                                value={peerQuery[key] || ''}
                                                onChange={(e) => searchPeers(key, e.target.value)}
                                                autoComplete="off"
                                            />
                                            {suggestions.length > 0 && (
                                                <ul
                                                    className="list-unstyled position-absolute bg-white border rounded shadow-sm mb-0 py-1 w-100"
                                                    style={{ zIndex: 200, top: '100%', maxHeight: '200px', overflowY: 'auto' }}
                                                >
                                                    {suggestions.map((e) => (
                                                        <li
                                                            key={e.id}
                                                            className="px-3 py-2 small"
                                                            role="button"
                                                            style={{ cursor: 'pointer' }}
                                                            onMouseDown={() => selectSuggestion(key, e)}
                                                        >
                                                            {e.user}
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </div>
                                        <button
                                            type="button"
                                            className="btn btn-success btn-sm btn-rounded px-3"
                                            disabled={!selectedPeer[key] || adding[key]}
                                            onClick={() => handleAddPeer(review, emp)}
                                        >
                                            {adding[key]
                                                ? <FontAwesomeIcon icon={solid('spinner')} spin />
                                                : <FontAwesomeIcon icon={solid('plus')} />
                                            }
                                        </button>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                )
            })}
        </div>
    )
}
